import React, { useEffect, useState } from "react";
import { observer } from "mobx-react-lite";
import { AdminPageWrapper } from "../AdminPageWrapper";
import {
  HStack,
  Stack,
  Text,
  Table,
  Spinner,
  Checkbox,
  Icon,
  ButtonGroup,
  IconButton,
} from "@chakra-ui/react";
import { AdminView } from "../../models/AdminView";
import { toast } from "../../../shared/views/CustomToaster";
import { PageControls } from "../../../pages/map/views/BottomBar/osa-way/PageControls";
import { AdminProjectApi } from "../../services/AdminProjectApi";
import { AdminProject } from "../../../project/models/Project";
import { Page } from "../../../shared/utils/http-utils";
import { Link } from "react-router-dom";
import { appRoutes } from "../../../../App";
import { AddIcon, ExternalLinkIcon } from "../../../shared/views/icons";
import { ProjectApi } from "../../../project/services/ProjectApi";

const pageSize = 20;

const AdminProjectsPageBase: React.FC = () => {
  const [pageIndex, setPageIndex] = useState(0);
  const [loading, setLoading] = useState(true);

  const [page, setPage] = useState<Page<AdminProject>>();

  const goToPage = async (pageNumber: number) => {
    setLoading(true);
    const projectsPage = await AdminProjectApi.page(pageSize, pageNumber).then(
      (res) => res.data
    );
    setPage(projectsPage);
    setLoading(false);
  };

  useEffect(() => {
    goToPage(pageIndex);
  }, [pageIndex]);

  const setExcludeFromAnalysis = async (
    projectId: string,
    excludeFromAnalysis: boolean
  ) => {
    await AdminProjectApi.put(projectId, { excludeFromAnalysis })
      .then((res) => {
        const updatedProject = res.data;
        const updatedData = page!.data.map((p) =>
          p.id === updatedProject.id ? updatedProject : p
        );
        setPage({ ...page!, data: updatedData });
        toast("success", "Project updated");
      })
      .catch((err) => {
        console.error(err);
        toast("error", "Failed to update project");
      });
  };

  const addProjectToLibrary = async (projectId: string) => {
    await ProjectApi.postAccountAccess(projectId)
      .then(() => {
        toast("success", "Project added");
        goToPage(pageIndex);
      })
      .catch(() => {
        toast("error", "Error. Probably because it's already in your library.");
      });
  };

  return (
    <AdminPageWrapper selectedView={AdminView.Projects}>
      <Stack px={6}>
        <Text fontSize="2xl">{AdminView.Projects}</Text>

        <HStack justifyContent={"end"}>
          <PageControls
            pageSize={pageSize}
            pageIndex={pageIndex}
            setPageIndex={setPageIndex}
            nItems={page?.meta?.totalElements ?? 0}
          />
        </HStack>

        {loading ? (
          <Spinner color={"primary.500"} />
        ) : (
          <Table.Root striped>
            <Table.Header>
              <Table.Row>
                <Table.ColumnHeader>Name</Table.ColumnHeader>
                <Table.ColumnHeader>City</Table.ColumnHeader>
                <Table.ColumnHeader>Coordinates</Table.ColumnHeader>
                <Table.ColumnHeader>Exclude from analysis</Table.ColumnHeader>
                <Table.ColumnHeader textAlign={"right"}>
                  Actions
                </Table.ColumnHeader>
              </Table.Row>
            </Table.Header>

            <Table.Body>
              {page?.data.map((project) => (
                <Table.Row key={project.id}>
                  <Table.Cell>
                    <Link to={appRoutes.project(project.id)}>
                      <HStack _hover={{ textDecor: "underline" }}>
                        <Text>{project.name}</Text>
                        <Icon as={ExternalLinkIcon} />
                      </HStack>
                    </Link>
                  </Table.Cell>
                  <Table.Cell>{project.city}</Table.Cell>
                  <Table.Cell>
                    {project.latitude}, {project.longitude}
                  </Table.Cell>
                  <Table.Cell>
                    <Checkbox.Root
                      size="sm"
                      colorPalette="primary"
                      checked={project.excludeFromAnalysis}
                      onCheckedChange={({ checked }) => {
                        setExcludeFromAnalysis(project.id, !!checked);
                      }}
                    >
                      <Checkbox.HiddenInput />
                      <Checkbox.Control />
                    </Checkbox.Root>
                  </Table.Cell>
                  <Table.Cell textAlign={"right"} py={0}>
                    <ButtonGroup size="sm">
                      <IconButton
                        title="Add to my library"
                        variant="outline"
                        colorPalette={"primary"}
                        onClick={() => addProjectToLibrary(project.id)}
                      >
                        <AddIcon />
                      </IconButton>
                    </ButtonGroup>
                  </Table.Cell>
                </Table.Row>
              ))}
            </Table.Body>
          </Table.Root>
        )}
      </Stack>
    </AdminPageWrapper>
  );
};

export const AdminProjectsPage = observer(AdminProjectsPageBase);
